import { MIX_REGISTERED_SLUGS } from "@/config/browserBoard";
import { findMixLineupEntry, type MixShare } from "@/config/mixLineup";
import { BoardSelectionCards } from "@/features/settings/components/sections/BoardSelectionCards";
import {
  defaultMixBoardSetting,
  MIX_SHARE_PERCENTS,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { Select, SettingRow } from "@/features/settings/ui";

interface MixSettingsProps {
  /**
   * ラインナップ順の登録済み総合カタログ対象slug。
   * 通常はruntime configから取るが、テストでは明示指定する。
   */
  registeredSlugs?: readonly string[];
}

function boardDisplayName(slug: string): string {
  const entry = findMixLineupEntry(slug);
  return entry ? `${entry.emoji} ${entry.label}` : slug;
}

function boardPlainName(slug: string): string {
  return findMixLineupEntry(slug)?.label ?? slug;
}

/**
 * 総合カタログの板構成設定。基本設定でカードから混ぜる板を選び、
 * 拡張設定(既定で閉じたアコーディオン)でONの板ごとの取り分を選ぶ。
 */
export const MixSettings: React.FunctionComponent<MixSettingsProps> = ({
  registeredSlugs = MIX_REGISTERED_SLUGS,
}: MixSettingsProps) => {
  const boardSettings = useMixStore((state) => state.boardSettings);
  const setBoardShare = useMixStore((state) => state.setBoardShare);

  const enabledSlugs = registeredSlugs.filter(
    (slug) => (boardSettings[slug] ?? defaultMixBoardSetting(slug)).enabled,
  );

  return (
    <section aria-label="統合カタログ設定" className="bg-transparent">
      <BoardSelectionCards registeredSlugs={registeredSlugs} compact />
      <details className="px-4 py-3">
        <summary className="cursor-pointer text-sm font-bold text-foreground">
          拡張設定
        </summary>
        <section
          className="mt-3 divide-y divide-border"
          aria-label="板ごとの拡張設定"
        >
          <ul
            aria-label="板ごとの表示割合"
            className="m-0 list-none divide-y divide-border p-0"
          >
            {enabledSlugs.map((slug) => {
              const setting =
                boardSettings[slug] ?? defaultMixBoardSetting(slug);
              return (
                <li key={slug}>
                  <SettingRow label={boardDisplayName(slug)}>
                    <Select
                      value={
                        setting.share === "uncapped"
                          ? "uncapped"
                          : String(setting.share)
                      }
                      onChange={(value: string): void =>
                        setBoardShare(slug, parseShare(value, setting.share))
                      }
                      aria-label={`${boardPlainName(slug)}の割合`}
                    >
                      <option value="uncapped">制御なし</option>
                      {[...MIX_SHARE_PERCENTS].reverse().map((pct) => (
                        <option key={pct} value={String(pct)}>
                          全体の{pct}%
                        </option>
                      ))}
                    </Select>
                  </SettingRow>
                </li>
              );
            })}
          </ul>
          <SettingRow description="制御なしの板は全体の並び(bump順など)にそのまま混ざります。%は1ページに混ざる枚数の上限の目安で、板のスレ数やNG設定により前後します。すべての板に%を付けた場合、1ページの枚数は取り分の合計までになります。" />
        </section>
      </details>
    </section>
  );
};

function parseShare(value: string, fallback: MixShare): MixShare {
  if (value === "uncapped") return "uncapped";
  return MIX_SHARE_PERCENTS.find((pct) => String(pct) === value) ?? fallback;
}
